import type { RouteOptions } from '@sosocio/creator-types/app';
import * as DialogService from 'services/dialog';
import {
	getSmartLinkAttributes,
	hasInheritedProjectCartUrl,
} from 'services/smartlink';
import {
	AppStateModule,
	ProductStateModule,
} from 'store';
import reportError, {
	isErrorReported,
	markErrorReported,
} from 'utils/report-error';
import {
	isNavigationFailure,
	NavigationFailureType,
	type RouteLocationGeneric,
	type RouteLocationNormalizedGeneric,
	type Router,
} from 'vue-router';
import { isRouteChunkLoadError } from '../../routes-modules/load-route-module';

interface RouteLoadRecovery {
	hasCompletion: boolean;
	retry: () => void;
}

interface RouteNavigationWorkflow {
	/** Cross-sell completion fills selected photos; a manual retry must keep this continuation. */
	onComplete: RouteOptions['onComplete'];
	retry: () => void;
}

interface RouteNavigationAttempt {
	active: boolean;
	workflow: RouteNavigationWorkflow;
}

const currentNavigations: WeakMap<Router, RouteLocationNormalizedGeneric> = new WeakMap();
const latestAttempts: WeakMap<Router, RouteNavigationAttempt> = new WeakMap();
const navigationAttempts: WeakMap<RouteLocationNormalizedGeneric, RouteNavigationAttempt> = new WeakMap();
const recoveries: WeakMap<Router, RouteLoadRecovery> = new WeakMap();

/**
 * Allows a browser reload only after all project work and transient cart state are safe.
 *
 * @param recovery - Failed navigation including any unfinished workflow continuation.
 * @returns Whether reloading can preserve the current project, workflow, and partner destination.
 */
function canReloadRoute(recovery: RouteLoadRecovery): boolean {
	return !recovery.hasCompletion
		&& ProductStateModule.getSaved === true
		&& !hasInheritedProjectCartUrl()
		&& getSmartLinkAttributes() === null;
}

/**
 * Checks navigation identity so a late asset error cannot affect a newer route.
 *
 * @param router - Application {@link Router} owning the navigation.
 * @param to - Normalized route supplied to the navigation hooks.
 * @returns Whether the route belongs to the current navigation, or tracking is not installed.
 */
export function isCurrentRouteNavigation(router: Router, to: RouteLocationNormalizedGeneric): boolean {
	return !currentNavigations.has(router) || currentNavigations.get(router) === to;
}

/**
 * Reloads the failed destination even when it only differs by its fragment.
 *
 * @param router - Application {@link Router} resolving the history base.
 * @param target - Failed route including its query and hash.
 */
function reloadRoute(router: Router, target: string): void {
	const href = router.resolve(target).href;
	const destination = new URL(
		href,
		window.location.href,
	);
	const current = new URL(window.location.href);

	if (destination.pathname === current.pathname && destination.search === current.search) {
		window.history.replaceState(
			window.history.state,
			'',
			href,
		);
		window.location.reload();
	} else {
		window.location.assign(href);
	}
}

/**
 * Consumes route asset rejections already presented by the router's error boundary.
 *
 * @param error - Navigation rejection, which may be unrelated to asset loading.
 */
export function consumeRouteLoadError(error: unknown): void {
	if (!isRouteChunkLoadError(error)) {
		throw error;
	}
}

/**
 * Presents persistent route asset failures with manual retry and safe browser reload actions.
 *
 * @param router - Application {@link Router} supplying failed destinations and navigation hooks.
 * @returns Unsubscribe function that also closes any outstanding recovery dialog.
 */
export function installRouteLoadRecovery(router: Router): () => void {
	let closeDialog: (() => void) | undefined;
	const clearRecovery = (): void => {
		const close = closeDialog;
		closeDialog = undefined;
		recoveries.delete(router);
		close?.();
	};
	const removeBeforeHandler = router.beforeEach((to) => {
		currentNavigations.set(
			router,
			to,
		);
		clearRecovery();
	});
	const removeErrorHandler = router.onError((error, to) => {
		if (!isCurrentRouteNavigation(
			router,
			to,
		)) {
			return;
		}

		if (isRouteChunkLoadError(error) && !isErrorReported(error) && reportError(
			error,
			'error',
		)) {
			markErrorReported(error);
		}

		clearRecovery();
		if (!isRouteChunkLoadError(error)) {
			return;
		}

		const attempt = navigationAttempts.get(to);
		const workflow = attempt?.active ? attempt.workflow : undefined;
		const recovery: RouteLoadRecovery = {
			hasCompletion: !!workflow?.onComplete,
			retry: workflow?.retry ?? (() => navigateWithRouteRecovery(
				router,
				to.fullPath,
				{
					replace: false,
				},
			)),
		};
		recoveries.set(
			router,
			recovery,
		);
		const retry = (): void => {
			clearRecovery();
			recovery.retry();
		};
		const buttons: DialogServiceButton[] = [
			{
				id: 'retry',
				text: window.App.router.$t('dialogButtonErrorRetry'),
				click: retry,
			},
		];

		if (canReloadRoute(recovery)) {
			buttons.unshift({
				id: 'reload',
				text: window.App.router.$t('dialogButtonReload'),
				click: () => {
					/** Save/upload state can change while the dialog is open. */
					if (!canReloadRoute(recovery)) {
						retry();
						return;
					}

					clearRecovery();
					reloadRoute(
						router,
						to.fullPath,
					);
				},
			});
		}

		let content = window.App.router.$t('dialogTextRouteLoadRecovery');

		if (recovery.hasCompletion || hasInheritedProjectCartUrl() || getSmartLinkAttributes() !== null) {
			content += `\n\n${window.App.router.$t('dialogTextRouteLoadRecoveryWorkflow')}`;
		} else if (ProductStateModule.getSaved !== true) {
			content += `\n\n${window.App.router.$t('dialogTextRouteLoadRecoveryUnsaved')}`;
		}

		closeDialog = DialogService.openErrorDialog({
			header: {
				title: window.App.router.$t(AppStateModule.online ? 'dialogHeaderError' : 'dialogHeaderOffline'),
			},
			body: {
				content,
			},
			footer: {
				buttons,
			},
			listeners: {
				close: () => {
					if (recoveries.get(router) === recovery) {
						closeDialog = undefined;
						recoveries.delete(router);
					}
				},
			},
		});
	});
	const removeAfterHandler = router.afterEach((to, _from, failure) => {
		/** Duplicated navigations cancel older imports without running beforeEach. */
		if (isNavigationFailure(
			failure,
			NavigationFailureType.duplicated,
		)) {
			currentNavigations.set(
				router,
				to,
			);
		}

		if (isCurrentRouteNavigation(
			router,
			to,
		)) {
			clearRecovery();
		}
	});

	return () => {
		removeBeforeHandler();
		removeErrorHandler();
		removeAfterHandler();
		clearRecovery();
		currentNavigations.delete(router);
		latestAttempts.delete(router);
	};
}

/**
 * Navigates with recovery that preserves the original method, target, and completion callback.
 *
 * @param router - Application {@link Router} performing navigation.
 * @param target - Route path including its query and hash.
 * @param options - Original {@link RouteOptions} for the navigation and successful completion.
 * @param consumeNavigationError - Existing caller-specific rejection policy; unexpected failures rethrow by default.
 */
export function navigateWithRouteRecovery(
	router: Router,
	target: string,
	options: RouteOptions,
	consumeNavigationError: (error: unknown) => void = consumeRouteLoadError,
): void {
	let completed = false;
	const workflow: RouteNavigationWorkflow = {
		onComplete: options.onComplete,
		retry,
	};

	/** Retries the same navigation, completing its workflow only once after success. */
	function retry(): void {
		const attempt: RouteNavigationAttempt = {
			active: true,
			workflow,
		};
		latestAttempts.set(
			router,
			attempt,
		);
		const destination = router.resolve(target).fullPath;
		let origin: RouteLocationGeneric | undefined;
		const removeWorkflowGuard = router.beforeEach((to) => {
			if (!origin && latestAttempts.get(router) !== attempt) {
				return;
			}

			/** Redirects retain the original normalized object; equal paths alone are not identity. */
			const source = to.redirectedFrom ?? to;

			if (!origin && source.fullPath === destination) {
				origin = source;
			}
			if (origin === source) {
				navigationAttempts.set(
					to,
					attempt,
				);
			}
		});
		const navigation = options.replace ? router.replace(target) : router.push(target);

		void navigation.then(
			(failure) => {
				removeWorkflowGuard();
				attempt.active = false;
				if (latestAttempts.get(router) === attempt) {
					latestAttempts.delete(router);
				}
				if (
					(!failure || isNavigationFailure(
						failure,
						NavigationFailureType.duplicated,
					))
					&& !completed
				) {
					completed = true;
					workflow.onComplete?.();
				}
			},
			(error: unknown) => {
				removeWorkflowGuard();
				attempt.active = false;
				if (latestAttempts.get(router) === attempt) {
					latestAttempts.delete(router);
				}

				consumeNavigationError(error);
			},
		);
	}

	retry();
}
